import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
} from '@mui/material';
import { Person as PersonIcon, Save as SaveIcon } from '@mui/icons-material';
import api from '../../services/api';
import { useSelector } from 'react-redux';

const TechnicianProfile = () => {
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [technicianData, setTechnicianData] = useState(null);
  const [formData, setFormData] = useState({
    phone: '',
    experience: '',
    specializations: [],
    current_location: '',
  });

  useEffect(() => {
    fetchTechnicianProfile();
  }, []);

  const fetchTechnicianProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/technicians/my-profile/');
      setTechnicianData(response.data);
      setFormData({
        phone: response.data.phone || '',
        experience: response.data.experience || '',
        specializations: response.data.specializations || [],
        current_location: response.data.current_location || '',
      });
    } catch (err) {
      setError('Failed to load profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/v1/technicians/my-profile/', formData);
      setSuccess('Profile updated successfully!');
      await fetchTechnicianProfile();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Avatar sx={{ width: 64, height: 64, mr: 2, bgcolor: 'primary.main' }}>
            <PersonIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Box>
            <Typography variant="h4">My Profile</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Read-only information */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={technicianData?.full_name || ''}
                disabled
                variant="filled"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee ID"
                value={technicianData?.employee_id || ''}
                disabled
                variant="filled"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={user?.email || ''}
                disabled
                variant="filled"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Status"
                value={technicianData?.status || ''}
                disabled
                variant="filled"
              />
            </Grid>

            {/* Editable fields */}
            <Grid item xs={12}>
              <Divider />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+44 123 456 7890"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Work Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                placeholder="e.g., 5 years"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Location"
                name="current_location"
                value={formData.current_location}
                onChange={handleChange}
                placeholder="e.g., Bay 1"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Specializations (comma-separated)"
                name="specializations"
                value={formData.specializations.join(', ')}
                onChange={(e) => {
                  const specs = e.target.value.split(',').map((s) => s.trim()).filter((s) => s);
                  setFormData((prev) => ({ ...prev, specializations: specs }));
                }}
                placeholder="e.g., General Mechanic, Brake Systems, Engine Diagnostics"
                multiline
                rows={2}
                helperText="Separate multiple specializations with commas"
              />
            </Grid>

            {/* Action buttons */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default TechnicianProfile;
